import React from 'react';
import { Slot } from '@radix-ui/react-slot';

// Utility function for combining class names (AlignUI pattern)
import { cn } from "./utils";

// Button Root Component (AlignUI pattern)
const ButtonRoot = React.forwardRef(({ 
  className = '', 
  variant = 'primary', 
  mode = 'filled',
  size = 'medium',
  asChild = false,
  children, 
  ...props 
}, ref) => {
  const Comp = asChild ? Slot : 'button';
  
  const baseClasses = "inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  
  const variants = {
    primary: {
      filled: "bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500 shadow-sm",
      stroke: "bg-transparent hover:bg-primary-50 text-primary-600 border border-primary-600 hover:border-primary-700 focus:ring-primary-500",
      lighter: "bg-primary-50 hover:bg-primary-100 text-primary-700 focus:ring-primary-500",
      ghost: "bg-transparent hover:bg-primary-50 text-primary-600 focus:ring-primary-500",
    },
    secondary: {
      filled: "bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500 shadow-sm",
      stroke: "bg-transparent hover:bg-gray-50 text-gray-600 border border-gray-300 hover:border-gray-400 focus:ring-gray-500",
      lighter: "bg-gray-50 hover:bg-gray-100 text-gray-700 focus:ring-gray-500",
      ghost: "bg-transparent hover:bg-gray-50 text-gray-600 focus:ring-gray-500",
    }
  };
  
  const sizes = {
    small: "px-3 py-1.5 text-sm rounded-md",
    medium: "px-4 py-2.5 text-sm rounded-lg",
    large: "px-6 py-3 text-base rounded-lg",
  };
  
  const classes = cn(
    baseClasses,
    variants[variant]?.[mode] || variants.primary.filled,
    sizes[size],
    className
  );
  
  return (
    <Comp
      ref={ref}
      className={classes}
      {...props}
    >
      {children}
    </Comp>
  );
});

// Button Icon Component (AlignUI pattern)
const ButtonIcon = React.forwardRef(({ 
  as: Component,
  className = '',
  ...props 
}, ref) => {
  if (!Component) {
    return null;
  }
  
  return (
    <Component
      ref={ref}
      className={cn("h-4 w-4", className)}
      {...props}
    />
  );
});

ButtonRoot.displayName = 'Button.Root';
ButtonIcon.displayName = 'Button.Icon';

// Export compound component (AlignUI pattern)
export const Root = ButtonRoot;
export const Icon = ButtonIcon;

// Default export for backward compatibility
const Button = ButtonRoot;
export default Button;
